import type {
  EvidenceStatement,
  FocusActivationResult,
  FocusDeviationRecord,
  IdeaParkingItem,
  PromiseEntry,
} from "../domain/types";

export interface TruthRiskProjection {
  has_material_unverified_statement: boolean;
  blocking_issue_count: number;
  due_for_review_count: number;
}

export interface FocusRiskProjection {
  has_mainline_deviation: boolean;
  has_new_project_request: boolean;
  resource_conflict_summary: string | null;
  needs_board_review_count: number;
}

export interface PromiseRiskProjection {
  has_material_promise_risk: boolean;
  has_critical_overdue: boolean;
  needs_resource_support: boolean;
}

export interface GovernanceRiskProjection {
  truth: TruthRiskProjection;
  focus: FocusRiskProjection;
  promise: PromiseRiskProjection;
}

export function projectTruthRisk(
  statements: readonly EvidenceStatement[],
  blockingIssueCount: number,
  now = new Date().toISOString(),
): TruthRiskProjection {
  const nowMs = new Date(now).getTime();
  return {
    has_material_unverified_statement: statements.some(
      (statement) =>
        statement.deletionState === "active" &&
        statement.status === "active" &&
        (statement.statementType === "hypothesis" || statement.userConfirmation === "unconfirmed"),
    ),
    blocking_issue_count: blockingIssueCount,
    due_for_review_count: statements.filter(
      (statement) =>
        statement.reviewDueAt !== undefined &&
        new Date(statement.reviewDueAt).getTime() < nowMs &&
        statement.deletionState === "active",
    ).length,
  };
}

export function projectFocusRisk(
  deviations: readonly FocusDeviationRecord[],
  ideas: readonly IdeaParkingItem[],
): FocusRiskProjection {
  const escalations = ideas.filter(
    (idea) =>
      idea.parkingStatus === "escalated_to_board" ||
      idea.activationReview?.result === ("escalate_to_board" satisfies FocusActivationResult),
  );
  const resourceConflictCount = ideas.filter(
    (idea) =>
      idea.deletionState === "active" &&
      Boolean(idea.resourceCost.trim() || idea.opportunityCost.trim()) &&
      idea.parkingStatus !== "archived" &&
      idea.parkingStatus !== "rejected",
  ).length;
  return {
    has_mainline_deviation: deviations.some(
      (deviation) =>
        deviation.deletionState === "active" && deviation.affectedCriticalBusinessAction,
    ),
    has_new_project_request: ideas.some(
      (idea) =>
        idea.deletionState === "active" &&
        (idea.parkingStatus === "under_review" || idea.parkingStatus === "escalated_to_board"),
    ),
    resource_conflict_summary:
      resourceConflictCount > 0
        ? `${resourceConflictCount} 项候选想法声明了资源或机会成本。`
        : null,
    needs_board_review_count: escalations.length,
  };
}

export function projectPromiseRisk(
  promises: readonly PromiseEntry[],
  now = new Date().toISOString(),
): PromiseRiskProjection {
  const nowMs = new Date(now).getTime();
  const active = promises.filter(
    (promise) =>
      promise.deletionState === "active" &&
      !["fulfilled", "cancelled", "archived"].includes(promise.promiseStatus) &&
      promise.confidentiality !== "highly_sensitive",
  );
  return {
    has_material_promise_risk: active.some(
      (promise) =>
        promise.materiality === "critical" ||
        promise.materiality === "high" ||
        promise.promiseStatus === "breached" ||
        promise.promiseStatus === "overdue",
    ),
    has_critical_overdue: active.some(
      (promise) =>
        promise.materiality === "critical" &&
        (promise.promiseStatus === "overdue" || new Date(promise.dueAt).getTime() < nowMs),
    ),
    needs_resource_support: active.some(
      (promise) =>
        (promise.materiality === "critical" || promise.materiality === "high") &&
        promise.promiseStatus === "renegotiation_needed",
    ),
  };
}

export function projectGovernanceRisk(
  input: {
    statements: readonly EvidenceStatement[];
    blockingTruthIssueCount: number;
    deviations: readonly FocusDeviationRecord[];
    ideas: readonly IdeaParkingItem[];
    promises: readonly PromiseEntry[];
  },
  now = new Date().toISOString(),
): GovernanceRiskProjection {
  return {
    truth: projectTruthRisk(input.statements, input.blockingTruthIssueCount, now),
    focus: projectFocusRisk(input.deviations, input.ideas),
    promise: projectPromiseRisk(input.promises, now),
  };
}

const FORBIDDEN_GOVERNANCE_PROJECTION_KEYS = new Set([
  "content",
  "rawText",
  "raw_text",
  "counterpartyAlias",
  "counterparty_alias",
  "description",
  "trustImpact",
  "trust_impact",
  "supportingEvidence",
  "supporting_evidence",
]);

export function governanceProjectionContainsSensitiveFields(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(governanceProjectionContainsSensitiveFields);
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).some(
      ([key, child]) =>
        FORBIDDEN_GOVERNANCE_PROJECTION_KEYS.has(key) ||
        governanceProjectionContainsSensitiveFields(child),
    );
  }
  return false;
}
